import DOMPurify from "dompurify";
import type { Config } from "dompurify";
import { logError } from "@/helpers/logError";

/**
 * Basic HTML sanitize options for simple rich text (headlines, basic content)
 * Allows common formatting tags and basic attributes
 */
export const BASIC_HTML_SANITIZE_OPTIONS: Config = {
	ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "a", "span", "div"],
	ALLOWED_ATTR: ["href", "target", "style", "class"]
};

/**
 * Rich HTML sanitize options for extended content (headers, footers)
 * Includes additional tags for lists and headings
 */
export const RICH_HTML_SANITIZE_OPTIONS: Config = {
	ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "a", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "span", "div"],
	ALLOWED_ATTR: ["href", "target", "style", "class"]
};

/**
 * Stricter than BASIC: drops list/heading tags and the style attribute to block
 * clickjacking via position:fixed overlays inside dialog messages.
 */
export const DIALOG_HTML_SANITIZE_OPTIONS: Config = {
	ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "a", "span", "div"],
	ALLOWED_ATTR: ["href", "target", "rel", "class"]
};

/**
 * What's New body: rich formatting (keeps lists/headings, unlike DIALOG) but WITHOUT the style OR
 * class attributes. Dropping style closes the position:fixed clickjacking-overlay vector; class is
 * dropped too because Vuetify ships global utility classes (.position-fixed/.w-100/.h-100) app-wide,
 * so an authored `class` could rebuild the same full-viewport overlay. The page supplies its own
 * typography, so authored classes aren't needed. Body is rendered via v-html in the dialog.
 */
export const WHATS_NEW_HTML_SANITIZE_OPTIONS: Config = {
	ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "a", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "span", "div"],
	ALLOWED_ATTR: ["href", "target", "rel"]
};

/**
 * Sanitize `html` with `options`, then post-process the output to inject
 * rel="noopener noreferrer" on any anchor that opens a NEW browsing context — i.e. any `target`
 * other than `_self` (blocks reverse-tabnabbing: a `target="_blank"` OR a named target like
 * `target="x"` otherwise gets `window.opener` access to the authenticated app).
 * Isolated from the shared DOMPurify singleton so ScribeReportViewer.vue's removeHook() call
 * can never strip it. Falls back to plain text (HTML-escaped) if DOMPurify or DOM manipulation
 * throws, so a hostile or exotic input can never blank-screen the surface.
 */
const sanitizeWithNoopenerAnchors = (html: string, options: Config, label: string): string => {
	try {
		const sanitized = DOMPurify.sanitize(html, options);
		const container = document.createElement("div");
		container.innerHTML = sanitized;
		// `a[target]:not([target="_self"])` — any target that opens a new context (_blank or a named
		// window), not just _blank, since a named target is equally exploitable for tabnabbing.
		container
			.querySelectorAll('a[target]:not([target="_self"])')
			.forEach((anchor) => anchor.setAttribute("rel", "noopener noreferrer"));
		return container.innerHTML;
	} catch (error) {
		logError(`${label}: DOMPurify failed, falling back to plain text`, error, { htmlLength: html.length });
		const fallback = document.createElement("div");
		fallback.textContent = html;
		return fallback.innerHTML;
	}
};

/**
 * Escape a plain-text value for safe interpolation into HTML we build ourselves (e.g. a dialog
 * message rendered with v-html). Sanitizing is not enough for this: the dialog allowlist keeps
 * `<br>`/`<strong>`, so a user-authored string dropped in raw could forge the surrounding message's
 * own formatting — a checklist template named "X<br><br>This template is used by 999 projects."
 * would render a second, fake count line beside the real one (LSP-15498).
 */
export const escapeHtmlText = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/** Sanitize HTML for use in dialogs (strict allowlist; anchors hardened with rel=noopener). */
export const sanitizeDialog = (html: string): string => sanitizeWithNoopenerAnchors(html, DIALOG_HTML_SANITIZE_OPTIONS, "sanitizeDialog");

/**
 * Sanitize a What's New rich-text body: keeps lists/headings, drops the style attribute, and
 * hardens blank-target anchors with rel="noopener noreferrer" (the body is HubDB-authored and
 * rendered via v-html inside the dashboard, so an authored `<a target="_blank">` must not gain
 * window.opener access).
 */
export const sanitizeWhatsNew = (html: string): string =>
	sanitizeWithNoopenerAnchors(html, WHATS_NEW_HTML_SANITIZE_OPTIONS, "sanitizeWhatsNew");
